import button from "./button";
import colors from "./colors";
import { css } from "emotion";

const phone = 400;
const phablet = 550;
const tablet = 750;
const desktop = 1000;
const hd = 1200;
const scale = 1.33;
const rem = 18;
const h6 = rem / scale / scale;
const h5 = rem / scale;
const h4 = rem;
const h3 = rem * scale;
const h2 = rem * scale * scale;
const h1 = rem * scale * scale * scale;
const hBig = rem * scale * scale * scale * scale;
const hSuper = rem * scale * scale * scale * scale * scale;
const hHuge = rem * scale * scale * scale * scale * scale * scale * scale;
const breakpoints = ["24em", "46.8em", "67.5em"];

// ...
const theme = {
  display: {
    fontFamily:
      "'adobe-garamond-pro', garamond,  'Gill Sans', apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    fontWeight: 400
  },
  sans: {
    fontFamily:
      "'europa', 'Gill Sans', apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    bold: 700
  },
  heading: {
    fontFamily:
      "'freight-neo-pro', 'Gill Sans', apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    bold: 700
  },
  mono: {
    fontFamily: "'mono45-headline','Lucida Console', Monaco, monospace ",
    normal: 400
  },

  system:
    "apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  huge: `${hHuge / rem}rem`,
  super: `${hSuper / rem}rem`,
  big: `${hBig / rem}rem`,
  h1: `${h1 / rem}rem`,
  h2: `${h2 / rem}rem`,
  h3: `${h3 / rem}rem`,
  h4: `${h4 / rem}rem`,
  h5: `${h5 / rem}rem`,
  h6: `${h6 / rem}rem`,
  bold: 700,
  normal: 400,
  light: 300,
  lineHeight1: 1,
  lineHeight2: 1.125,
  lineHeight3: 1.25,
  lineHeight4: 1.5,
  lineHeight5: 1.75,
  lineHeightCompressed: 0.8,
  breakpoints,
  maxWidth1: breakpoints[0],
  maxWidth2: breakpoints[1],
  maxWidth3: breakpoints[2],
  maxWidth4: breakpoints[3],
  spaceMicro: ".25rem",
  space1: ".5rem",
  space2: "1rem",
  space3: "2rem",
  space4: "4rem",
  space5: "8rem",
  spaceHuge: "10rem",
  phone,
  phablet,
  tablet,
  desktop,
  hd,
  phoneEm: `${phone / 16}em`,
  phabletEm: `${phablet / 16}em`,
  tabletEm: `${tablet / 16}em`,
  desktopEm: `${desktop / 16}em`,
  hdEm: `${hd / 16}em`
};

const merge = (a, b) =>
  Object.assign(
    {},
    a,
    b,
    Object.keys(b).reduce(
      (obj, key) =>
        Object.assign(obj, {
          [key]:
            a[key] !== null && typeof a[key] === "object"
              ? merge(a[key], b[key])
              : b[key]
        }),
      {}
    )
  );

const isPx = m => (typeof m === "number" ? `${m}px` : m);
const mqMin = n => `@media screen and (min-width: ${isPx(n)})`;
const mqMax = n => `@media screen and (max-width: ${isPx(n)})`;
const mqBetween = (n, m) =>
  `@media screen and (min-width: ${isPx(n)} and (max-width: ${isPx(m)})`;

function TooManyStylesException(message) {
  this.name = "ResponsiveStyleError";
  this.message = message;
}

const rs = (...styles) => {
  if (styles.length > breakpoints.length + 1) {
    throw new TooManyStylesException(
      `Too many styles. There are ${breakpoints.length +
        1} breakpoints (incl. default), but ${styles.length} styles passed`
    );
  }

  return css(
    styles.reduce((acc, x, i) => {
      if (i === 0) {
        return x;
      }
      return { ...acc, [mqMin(breakpoints[i - 1])]: x };
    })
  );
};

const ratio = (a, b) => a / b;
const mxAuto = () => `margin-left: "auto", margin-right: "auto"`;
const mx = space => `margin-left: ${space}, margin-right: ${space}`;
const my = space => ({ marginTop: space, marginBottom: space });
const px = space => ({ paddingLeft: space, paddingRight: space });
const py = space => ({ paddingTop: space, paddingBottom: space });

const AbsOverflow = css`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10;
`;

export {
  theme,
  button,
  colors,
  rs,
  ratio,
  mxAuto,
  mx,
  my,
  px,
  py,
  mqMin,
  mqMax,
  mqBetween,
  AbsOverflow
};



// WEBPACK FOOTER //
// ./src/styles/theme.js